import * as React from "react";
import { Lang, translate } from "./i18n";

type I18nContextValue = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  t: (key: string) => string;
};

const I18nContext = React.createContext<I18nContextValue | undefined>(undefined);

const STORAGE_KEY = "tk_lang";

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = React.useState<Lang>(() => {
    const raw = (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY)) || "";
    return raw === "de" ? "de" : "en";
  });

  const setLang = React.useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const toggleLang = React.useCallback(() => {
    setLang(lang === "en" ? "de" : "en");
  }, [lang, setLang]);

  const t = React.useCallback(
    (key: string) => translate(lang, key),
    [lang]
  );

  const value = React.useMemo<I18nContextValue>(
    () => ({ lang, setLang, toggleLang, t }),
    [lang, setLang, toggleLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
